import React, { useEffect, useState, useRef } from 'react';
import {
  collection, query, orderBy, onSnapshot,
  doc, updateDoc, serverTimestamp
} from 'firebase/firestore';
import { orderDb as db, orderAuth } from '../firebase';
import toast from 'react-hot-toast';
import { playOrderBell, requestNotificationPermission, showOrderNotification } from '../utils/orderSound';

const COLLECTION = 'orders';
const STATUS_FLOW  = ['confirmed','preparing','out for delivery','delivered'];
const STATUS_LABEL = {
  'confirmed':       'New Order',
  'preparing':       'Preparing',
  'out for delivery':'Out for Delivery',
  'delivered':       'Delivered',
};
const STATUS_COLOR = {
  'confirmed':       'tag-red',
  'preparing':       'tag-amber',
  'out for delivery':'tag-blue',
  'delivered':       'tag-green',
};

function normalize(raw) {
  return {
    id:            raw.id,
    orderId:       raw.orderId       || raw.id,
    status:        raw.status        || 'confirmed',
    paymentMode:   raw.paymentMode   || raw.payment_mode   || (raw.paymentId ? 'online' : 'cod'),
    paymentStatus: raw.paymentId     ? 'paid' : (raw.paymentStatus || 'pending'),
    subtotal:      raw.subtotal      || 0,
    deliveryFee:   raw.deliveryFee   || 49,
    gst:           raw.tax           || raw.gst || 0,
    total:         raw.total         || raw.totalAmount || raw.amount || 0,
    createdAt:     raw.createdAt,
    customer: raw.customer || {
      name:     raw.customerName || raw.name    || '',
      phone:    raw.phone        || raw.mobile  || '',
      address:  raw.address      || raw.houseNo || '',
      city:     raw.city         || '',
      pincode:  raw.pincode      || raw.pin     || '',
      landmark: raw.landmark     || '',
    },
    items: (raw.items || raw.cartItems || raw.orderItems || []).map(i => ({
      name:   i.label    || i.name     || i.itemName || '',
      qty:    i.qty      || i.quantity || 1,
      price:  i.price    || 0,
      addons: i.addons   || [],
    })),
  };
}

export default function OnlineOrders() {
  const [orders, setOrders]     = useState([]);
  const [filter, setFilter]     = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [loadError, setLoadError]     = useState('');

  const knownIds    = useRef(new Set());
  const initialized = useRef(false);

  useEffect(() => {
    requestNotificationPermission();

    let unsub = () => {}; let cancelled = false;
    (async () => {
      // wait until the website-orders sign-in state is known (so a signed-in staff account is used when it exists)
      try { await orderAuth.authStateReady(); } catch (e) { /* ignore */ }
      if (cancelled) return;
    const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
    unsub = onSnapshot(q, snap => {
      setLoadError('');
      const fetched = snap.docs.map(d => normalize({ id: d.id, ...d.data() }));
      setOrders(fetched);
      setLoading(false);

      if (!initialized.current) {
        fetched.forEach(o => knownIds.current.add(o.id));
        initialized.current = true;
        return;
      }

      const newOrders = fetched.filter(o =>
        !knownIds.current.has(o.id) &&
        (o.status === 'confirmed' || o.status === 'New' || o.status === 'new')
      );

      if (newOrders.length > 0) {
        newOrders.forEach(o => knownIds.current.add(o.id));
        if (soundEnabled) playOrderBell(3);
        toast(`🔔 ${newOrders.length} new order${newOrders.length > 1 ? 's' : ''} from vippizza.in!`, {
          duration: 6000,
          style: { background: '#D85A30', color: '#fff', fontWeight: 600, fontSize: 14 },
          icon: '🍕',
        });
        showOrderNotification('vippizza.in', newOrders.length);
      } else {
        fetched.forEach(o => knownIds.current.add(o.id));
      }
    }, err => {
      const denied = err.code === 'permission-denied';
      setLoadError(denied
        ? 'Website orders are blocked by the Firestore security rules of the vip-pizza-order project (Missing or insufficient permissions). The rules need to allow the billing app to read and update the "orders" collection.'
        : 'Cannot load orders: ' + err.message);
      setLoading(false);
    });

    })();
    return () => { cancelled = true; unsub(); };
  }, [soundEnabled]);

  const advance = async (id, cur) => {
    const next = STATUS_FLOW[STATUS_FLOW.indexOf(cur) + 1];
    if (!next) return;
    await updateDoc(doc(db, COLLECTION, id), { status: next, updatedAt: serverTimestamp() });
    toast.success('Marked: ' + STATUS_LABEL[next]);
  };

  const filtered = filter === 'all' ? orders : orders.filter(o => o.status === filter);
  const today    = orders.filter(o => { const d = o.createdAt?.toDate?.(); return d && new Date().toDateString() === d.toDateString(); });
  const newCount = orders.filter(o => ['confirmed','new','New'].includes(o.status)).length;

  if (loading) return (
    <div style={{ textAlign:'center', padding:60, color:'#aaa' }}>
      Loading orders from vippizza.in…
    </div>
  );

  return (
    <div>
      {loadError && (
        <div style={{ background:'#fdecea', border:'1px solid #f5c2bd', color:'#b3261e', borderRadius:9, padding:'12px 15px', fontSize:13, marginBottom:14 }}>
          ⚠️ {loadError}
        </div>
      )}
      <div className="grid4" style={{ marginBottom:16 }}>
        {[
          ['Today Orders',   today.length],
          ['Today Revenue',  '₹'+today.reduce((a,o)=>a+(o.total||0),0).toLocaleString()],
          ['New / Pending',  newCount],
          ['Total Orders',   orders.length],
        ].map(([l,v]) => (
          <div key={l} className="metric">
            <div className="metric-label">{l}</div>
            <div className="metric-val" style={{ fontSize:18, color:l==='New / Pending'&&+v>0?'#e53935':undefined }}>{v}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:14, alignItems:'center', flexWrap:'wrap' }}>
        {newCount > 0 && (
          <div style={{ flex:1, background:'#FAECE7', border:'1px solid #f0c0a0', borderRadius:9,
                        padding:'10px 15px', fontSize:13, color:'#993C1D' }}>
            🔔 <b>{newCount} new order{newCount>1?'s':''}</b> from vippizza.in waiting
          </div>
        )}
        <button onClick={() => {
            setSoundEnabled(p => !p);
            if (!soundEnabled) playOrderBell(1);
          }}
          style={{ padding:'8px 14px', borderRadius:8, border:'1px solid #ddd', cursor:'pointer',
                   background:soundEnabled?'#EAF3DE':'#f8f8f8',
                   color:soundEnabled?'#3B6D11':'#888', fontSize:13, fontWeight:500, whiteSpace:'nowrap' }}>
          {soundEnabled ? '🔔 Sound ON' : '🔕 Sound OFF'}
        </button>
        <button onClick={() => playOrderBell(3)}
          style={{ padding:'8px 14px', borderRadius:8, border:'1px solid #ddd', cursor:'pointer',
                   background:'#fff', fontSize:13, whiteSpace:'nowrap' }}>
          🔔 Test Bell
        </button>
      </div>

      <div style={{ display:'flex', gap:7, marginBottom:14, flexWrap:'wrap' }}>
        {['all', ...STATUS_FLOW].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding:'5px 12px', borderRadius:20, fontSize:12, cursor:'pointer', border:'1px solid',
            borderColor:filter===f?'#D85A30':'#ddd', background:filter===f?'#FAECE7':'#fff',
            color:filter===f?'#993C1D':'#555', textTransform:'capitalize',
          }}>{STATUS_LABEL[f] || f}</button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign:'center', padding:40, color:'#bbb' }}>No orders found</div>
      )}

      {filtered.map(o => (
        <div key={o.id} className="card" style={{ marginBottom:10, padding:'14px 16px',
          borderLeft: ['confirmed','new','New'].includes(o.status) ? '4px solid #D85A30' : '1px solid #e8e8e8' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div style={{ flex:1 }}>
              <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:7, flexWrap:'wrap' }}>
                <span style={{ fontWeight:600, fontSize:14 }}>{o.orderId}</span>
                <span className={`tag ${STATUS_COLOR[o.status]||'tag-gray'}`}>{STATUS_LABEL[o.status]||o.status}</span>
                <span className="tag tag-blue">🌐 vippizza.in</span>
                <span className={`tag ${o.paymentStatus==='paid'?'tag-green':'tag-amber'}`}>
                  {(o.paymentMode||'').toUpperCase()} · {o.paymentStatus}
                </span>
              </div>
              <div style={{ fontSize:13, marginBottom:3 }}>
                👤 <b>{o.customer.name}</b> &nbsp;·&nbsp; 📞 {o.customer.phone}
              </div>
              <div style={{ fontSize:12, color:'#888', marginBottom:7 }}>
                📍 {o.customer.address}{o.customer.city?', '+o.customer.city:''}
                {o.customer.pincode?' – '+o.customer.pincode:''}
                {o.customer.landmark?` (Near: ${o.customer.landmark})`:''}
              </div>
              <div style={{ fontSize:13, color:'#555', cursor:'pointer' }}
                onClick={() => setExpanded(expanded===o.id?null:o.id)}>
                🍕 {o.items.map(i=>i.name+(i.qty>1?' x'+i.qty:'')).join(', ')}
                <span style={{ color:'#D85A30', marginLeft:6 }}>{expanded===o.id?'▲ hide':'▼ details'}</span>
              </div>
              {expanded===o.id && (
                <div style={{ marginTop:9, padding:11, background:'#f8f8f8', borderRadius:8, fontSize:12 }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead><tr>{['Item','Qty','Price'].map(h=><th key={h} style={{ textAlign:'left', paddingBottom:6, color:'#888', fontWeight:500 }}>{h}</th>)}</tr></thead>
                    <tbody>{o.items.map((item,i)=>(
                      <tr key={i}>
                        <td style={{ paddingBottom:4 }}>{item.name}</td>
                        <td>{item.qty}</td>
                        <td>₹{item.price}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                  <div className="divider" style={{ margin:'8px 0' }} />
                  {[['Subtotal','₹'+o.subtotal],['Delivery','₹'+o.deliveryFee],['Tax','₹'+o.gst],['Total','₹'+o.total]].map(([l,v])=>(
                    <div key={l} style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                      <span style={{ color:'#888' }}>{l}</span>
                      <span style={{ fontWeight:l==='Total'?600:400 }}>{v}</span>
                    </div>
                  ))}
                  <div style={{ marginTop:6, color:'#aaa', fontSize:11 }}>
                    Placed: {o.createdAt?.toDate?.()?.toLocaleString?.()||'Just now'}
                  </div>
                </div>
              )}
            </div>
            <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8, marginLeft:16 }}>
              <div style={{ fontWeight:700, fontSize:16 }}>₹{o.total}</div>
              {STATUS_FLOW.indexOf(o.status) < STATUS_FLOW.length-1 && (
                <button className="btn btn-primary" style={{ fontSize:12, padding:'5px 12px', whiteSpace:'nowrap' }}
                  onClick={() => advance(o.id, o.status)}>
                  → {STATUS_LABEL[STATUS_FLOW[STATUS_FLOW.indexOf(o.status)+1]]}
                </button>
              )}
              {o.status==='delivered' && <span style={{ fontSize:12, color:'#3B6D11' }}>✅ Done</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}